import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
  verifyIdentity,
  validateSession,
  logout as apiLogout,
  getClientDetails,
  getUnmaskedDebtSummary,
  refreshSession,
} from '../services/api';

/**
 * Session state shape:
 * - status: 'unverified' | 'verifying' | 'verified' | 'expired' | 'locked-out'
 * - sessionToken: string | null
 * - clientName: string | null
 * - clientFirstName: string | null
 * - email: string | null
 * - phone: string | null
 * - idNumber: string | null
 * - loanApplicationId: string | null
 * - linkToken: string | null
 * - attemptsRemaining: number
 * - error: string | null
 * - clientDetails: object | null (unmasked client details from backend)
 * - unmaskedClientData: object | null (unmasked debt summary)
 */

const SESSION_STORAGE_KEY = 'dola_session_token';
const SESSION_EXPIRY_KEY = 'dola_session_expiry';
const SESSION_REFRESH_TOKEN_KEY = 'dola_refresh_token';
const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 minutes
const SESSION_REFRESH_INTERVAL_MS = 25 * 60 * 1000; // Refresh at 25 minutes (before 30-min expiry)

const SessionContext = createContext(null);

/**
 * Provides session state and actions to all child components.
 * Manages:
 * - ID verification flow (max 5 attempts before lockout)
 * - Session token persistence (sessionStorage)
 * - Session expiry checking
 * - Contact detail propagation across sections
 * - Post-verification data unmasking (re-fetches with session token)
 */
export function SessionProvider({ children, linkToken, loanApplicationId }) {
  const [state, setState] = useState({
    status: 'unverified',
    sessionToken: null,
    clientName: null,
    clientFirstName: null,
    email: null,
    phone: null,
    idNumber: null,
    attemptsRemaining: 5,
    error: null,
    clientDetails: null,
    unmaskedClientData: null,
  });

  const sessionCheckTimerRef = useRef(null);
  const sessionRefreshTimerRef = useRef(null);

  /**
   * Fetch unmasked client details and debt summary after verification.
   * Called after successful ID verification or session restoration.
   * Updates session state with full personal details.
   */
  const fetchUnmaskedData = useCallback(async (sessionToken) => {
    try {
      // Fetch unmasked client details and debt summary in parallel
      const [details, summary] = await Promise.all([
        getClientDetails(linkToken, sessionToken),
        getUnmaskedDebtSummary(linkToken, sessionToken),
      ]);

      setState(prev => ({
        ...prev,
        clientDetails: details,
        clientFirstName: details.clientFirstName || prev.clientFirstName,
        clientName: details.clientName || prev.clientName,
        email: details.email || prev.email,
        phone: details.phoneNumber || prev.phone,
        idNumber: details.idNumber || prev.idNumber,
        unmaskedClientData: summary,
      }));
    } catch (err) {
      // Non-critical: the portal still works with masked data
      console.warn('Failed to fetch unmasked client details:', err);
    }
  }, [linkToken]);

  // Try to restore session from sessionStorage on mount
  useEffect(() => {
    const storedToken = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const storedExpiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);

    if (storedToken && storedExpiry) {
      const expiryTime = parseInt(storedExpiry, 10);
      if (Date.now() < expiryTime) {
        // Session not expired - validate with backend
        validateSession(storedToken)
          .then((sessionData) => {
            setState(prev => ({
              ...prev,
              status: 'verified',
              sessionToken: storedToken,
              clientName: sessionData.clientName || null,
              clientFirstName: sessionData.clientFirstName || null,
              email: sessionData.email || null,
              phone: sessionData.phoneNumber || null,
              idNumber: sessionData.idNumber || null,
              loanApplicationId: sessionData.loanApplicationId || loanApplicationId,
            }));

            // Re-fetch unmasked data with restored session
            fetchUnmaskedData(storedToken);
          })
          .catch(() => {
            // Session invalid on backend - clear and require re-verification
            clearStoredSession();
            setState(prev => ({
              ...prev,
              status: 'expired',
              sessionToken: null,
              error: 'Your session has expired. Please verify your identity again.',
            }));
          });
      } else {
        // Session expired locally
        clearStoredSession();
        setState(prev => ({
          ...prev,
          status: 'expired',
          error: 'Your session has expired. Please verify your identity again.',
        }));
      }
    }
  }, [loanApplicationId, fetchUnmaskedData]);

  // Set up periodic session validity check
  useEffect(() => {
    if (state.status === 'verified' && state.sessionToken) {
      sessionCheckTimerRef.current = setInterval(() => {
        const storedExpiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);
        if (storedExpiry && Date.now() >= parseInt(storedExpiry, 10)) {
          clearStoredSession();
          setState(prev => ({
            ...prev,
            status: 'expired',
            sessionToken: null,
            error: 'Your session has expired. Please verify your identity again.',
          }));
        }
      }, 60_000); // Check every minute
    }

    return () => {
      if (sessionCheckTimerRef.current) {
        clearInterval(sessionCheckTimerRef.current);
      }
    };
  }, [state.status, state.sessionToken]);

  // Set up automatic session refresh before expiry
  useEffect(() => {
    if (state.status !== 'verified' || !state.sessionToken) return;

    const storedRefreshToken = sessionStorage.getItem(SESSION_REFRESH_TOKEN_KEY);
    if (!storedRefreshToken) return;

    // Calculate time until refresh: refresh at 25 min mark
    const storedExpiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);
    const expiryTime = storedExpiry ? parseInt(storedExpiry, 10) : Date.now() + SESSION_DURATION_MS;
    const timeUntilRefresh = Math.max(
      (expiryTime - Date.now()) - (SESSION_DURATION_MS - SESSION_REFRESH_INTERVAL_MS),
      0
    );

    sessionRefreshTimerRef.current = setTimeout(async () => {
      const currentRefreshToken = sessionStorage.getItem(SESSION_REFRESH_TOKEN_KEY);
      if (!currentRefreshToken) return;

      try {
        const result = await refreshSession(currentRefreshToken);

        if (result.success && result.sessionToken) {
          // Calculate new expiry from server expiresAt or fall back to 30 min
          const newExpiry = result.expiresAt
            ? new Date(result.expiresAt).getTime()
            : Date.now() + SESSION_DURATION_MS;

          sessionStorage.setItem(SESSION_STORAGE_KEY, result.sessionToken);
          sessionStorage.setItem(SESSION_EXPIRY_KEY, newExpiry.toString());
          if (result.refreshToken) {
            sessionStorage.setItem(SESSION_REFRESH_TOKEN_KEY, result.refreshToken);
          }

          setState(prev => ({
            ...prev,
            sessionToken: result.sessionToken,
          }));
        } else {
          // Refresh failed — clear session
          clearStoredSession();
          setState(prev => ({
            ...prev,
            status: 'expired',
            sessionToken: null,
            error: 'Your session has expired. Please verify your identity again.',
          }));
        }
      } catch {
        // Refresh token expired or invalid — clear session
        clearStoredSession();
        setState(prev => ({
          ...prev,
          status: 'expired',
          sessionToken: null,
          error: 'Your session has expired. Please verify your identity again.',
        }));
      }
    }, timeUntilRefresh);

    return () => {
      if (sessionRefreshTimerRef.current) {
        clearTimeout(sessionRefreshTimerRef.current);
      }
    };
  }, [state.status, state.sessionToken]);

  /**
   * Attempt ID verification.
   * On success, creates a session and fetches unmasked client details.
   * @param {string} idLastFourDigits - Last 4 digits of SA ID number
   * @returns {Promise<boolean>} Whether verification was successful
   */
  const verify = useCallback(async (idLastFourDigits) => {
    setState(prev => ({ ...prev, status: 'verifying', error: null }));

    try {
      const result = await verifyIdentity(linkToken, idLastFourDigits);

      if (result.success) {
        const expiryTime = result.expiresAt
          ? new Date(result.expiresAt).getTime()
          : Date.now() + SESSION_DURATION_MS;
        sessionStorage.setItem(SESSION_STORAGE_KEY, result.sessionToken);
        sessionStorage.setItem(SESSION_EXPIRY_KEY, expiryTime.toString());
        if (result.refreshToken) {
          sessionStorage.setItem(SESSION_REFRESH_TOKEN_KEY, result.refreshToken);
        }

        setState(prev => ({
          ...prev,
          status: 'verified',
          sessionToken: result.sessionToken,
          clientName: result.clientName || null,
          clientFirstName: result.clientFirstName || null,
          email: result.email || null,
          phone: result.phoneNumber || null,
          attemptsRemaining: 5,
          error: null,
        }));

        // Fetch unmasked data (debt summary) after successful verification
        fetchUnmaskedData(result.sessionToken);

        return true;
      }

      // Verification failed
      setState(prev => ({
        ...prev,
        status: result.isLockedOut ? 'locked-out' : 'unverified',
        attemptsRemaining: result.attemptsRemaining ?? prev.attemptsRemaining,
        error: result.message || 'Verification failed. Please try again.',
      }));

      return false;
    } catch (err) {
      // Handle HTTP error responses
      if (err.status === 423) {
        setState(prev => ({
          ...prev,
          status: 'locked-out',
          attemptsRemaining: 0,
          error: err.data?.message || 'Your account has been locked due to too many failed attempts. Please contact us for assistance.',
        }));
        return false;
      }

      if (err.status === 401) {
        const remaining = err.data?.attemptsRemaining;
        setState(prev => ({
          ...prev,
          status: 'unverified',
          attemptsRemaining: remaining ?? prev.attemptsRemaining,
          error: err.data?.message || 'The details you entered do not match our records.',
        }));
        return false;
      }

      setState(prev => ({
        ...prev,
        status: 'unverified',
        error: 'Something went wrong. Please try again.',
      }));

      return false;
    }
  }, [linkToken, fetchUnmaskedData]);

  /**
   * End the current session.
   */
  const endSession = useCallback(async () => {
    if (state.sessionToken) {
      try {
        await apiLogout(state.sessionToken);
      } catch {
        // Ignore logout errors - clear locally regardless
      }
    }

    clearStoredSession();
    setState(prev => ({
      ...prev,
      status: 'unverified',
      sessionToken: null,
      clientName: null,
      clientFirstName: null,
      email: null,
      phone: null,
      idNumber: null,
      clientDetails: null,
      unmaskedClientData: null,
      error: null,
    }));
  }, [state.sessionToken]);

  /**
   * Refresh the session expiry timer (called on user activity).
   */
  const touchSession = useCallback(() => {
    if (state.sessionToken) {
      const expiryTime = Date.now() + SESSION_DURATION_MS;
      sessionStorage.setItem(SESSION_EXPIRY_KEY, expiryTime.toString());
    }
  }, [state.sessionToken]);

  /**
   * Update contact details in session state.
   * Contact changes propagate to all sections via context.
   */
  const updateContact = useCallback((email, phone) => {
    setState(prev => ({
      ...prev,
      email: email !== undefined ? email : prev.email,
      phone: phone !== undefined ? phone : prev.phone,
    }));
  }, []);

  /**
   * Clear expired/invalid session error and go back to unverified state.
   */
  const clearError = useCallback(() => {
    setState(prev => ({
      ...prev,
      status: prev.status === 'locked-out' ? 'locked-out' : 'unverified',
      error: null,
    }));
  }, []);

  const contextValue = {
    // State
    ...state,
    linkToken,
    loanApplicationId,
    isVerified: state.status === 'verified',
    isVerifying: state.status === 'verifying',
    isLockedOut: state.status === 'locked-out',
    isExpired: state.status === 'expired',
    needsVerification: state.status === 'unverified' || state.status === 'expired',

    // Actions
    verify,
    endSession,
    touchSession,
    updateContact,
    clearError,
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
}

/**
 * Hook to access session context.
 * Must be used within a SessionProvider.
 * @returns {Object} Session state and actions
 */
export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}

function clearStoredSession() {
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
  sessionStorage.removeItem(SESSION_EXPIRY_KEY);
  sessionStorage.removeItem(SESSION_REFRESH_TOKEN_KEY);
}

export default SessionContext;
